import { createReducer } from '@reduxjs/toolkit';
import { logout } from '../types';
import {
  fetchBoards,
  createBoard,
  updateBoard,
  deleteBoard,
  fetchBoardTemplates,
  fetchStages,
  createStage,
  updateStage,
  deleteStage,
  reorderStages,
  fetchStatuses,
  createStatus,
  updateStatus,
  deleteStatus,
  reorderStatuses,
  fetchBoardTasks,
  createBoardTask,
  updateBoardTask,
  deleteBoardTask,
  fetchArchivedTasks,
  archiveBoardTask,
  restoreBoardTask,
  setActiveBoard,
} from '../actions/taskboard';
import type { TaskboardState } from '../types/taskboard';

const initialState: TaskboardState = {
  boardsByChannel: {},
  templatesByChannel: {},
  stagesByBoard: {},
  statusesByBoard: {},
  tasksByBoard: {},
  archivedByBoard: {},
  activeBoardId: null,
  boardsLoading: false,
  templatesLoading: false,
  columnsLoading: false,
  tasksLoading: false,
  archivedLoading: false,
  archivedError: null,
  error: null,
  templatesError: null,
};

/**
 * Where an optimistic column move stashes the pre-flight values so a failed
 * PATCH can be rolled back.
 *
 * A plain string key rather than a symbol: this lives on state Redux serialises
 * for devtools, and it is deliberately stripped on `.fulfilled` (the server
 * echo replaces the whole task) and on `.rejected` (after restoring).
 */
const OPTIMISTIC = '__optimisticPrevious' as const;

/** Replace an item in a bucket by id, leaving siblings untouched. */
function replaceById<T extends { id: number }>(list: T[] | undefined, item: T) {
  if (!list) return [item];
  const idx = list.findIndex((entry) => entry.id === item.id);
  if (idx === -1) return [...list, item];
  list[idx] = item;
  return list;
}

export const taskboardReducer = createReducer(initialState, (builder) => {
  builder
    // boards
    .addCase(fetchBoards.pending, (state) => {
      state.boardsLoading = true;
      state.error = null;
    })
    .addCase(fetchBoards.fulfilled, (state, action) => {
      const { channelId, boards } = action.payload;
      state.boardsLoading = false;
      state.boardsByChannel[channelId] = boards;
      // Auto-select so consumers get a usable board without a second round-trip
      if (state.activeBoardId === null && boards.length > 0) {
        state.activeBoardId = boards[0].id;
      }
    })
    .addCase(fetchBoards.rejected, (state, action) => {
      state.boardsLoading = false;
      state.error = action.payload as string;
    })
    .addCase(createBoard.fulfilled, (state, action) => {
      const { channelId, board } = action.payload;
      const boards = state.boardsByChannel[channelId] ?? [];
      boards.push(board);
      state.boardsByChannel[channelId] = boards;
      if (state.activeBoardId === null) state.activeBoardId = board.id;
    })
    .addCase(updateBoard.fulfilled, (state, action) => {
      const { channelId, board } = action.payload;
      state.boardsByChannel[channelId] = replaceById(
        state.boardsByChannel[channelId],
        board
      );
    })
    .addCase(deleteBoard.fulfilled, (state, action) => {
      const { channelId, boardId } = action.payload;
      state.boardsByChannel[channelId] = (
        state.boardsByChannel[channelId] ?? []
      ).filter((board) => board.id !== boardId);
      delete state.stagesByBoard[boardId];
      delete state.statusesByBoard[boardId];
      delete state.tasksByBoard[boardId];
      if (state.activeBoardId === boardId) state.activeBoardId = null;
    })

    // board templates
    .addCase(fetchBoardTemplates.pending, (state) => {
      state.templatesLoading = true;
      state.templatesError = null;
    })
    .addCase(fetchBoardTemplates.fulfilled, (state, action) => {
      const { channelId, templates } = action.payload;
      state.templatesLoading = false;
      state.templatesByChannel[channelId] = templates;
    })
    .addCase(fetchBoardTemplates.rejected, (state, action) => {
      state.templatesLoading = false;
      state.templatesError = action.payload as string;
    })

    // stages
    .addCase(fetchStages.pending, (state) => {
      state.columnsLoading = true;
      state.error = null;
    })
    .addCase(fetchStages.fulfilled, (state, action) => {
      state.columnsLoading = false;
      state.stagesByBoard[action.payload.boardId] = action.payload.stages;
    })
    .addCase(fetchStages.rejected, (state, action) => {
      state.columnsLoading = false;
      state.error = action.payload as string;
    })
    .addCase(createStage.fulfilled, (state, action) => {
      const { boardId, stage } = action.payload;
      const stages = state.stagesByBoard[boardId] ?? [];
      stages.push(stage);
      state.stagesByBoard[boardId] = stages;
    })
    .addCase(updateStage.fulfilled, (state, action) => {
      const { boardId, stage } = action.payload;
      state.stagesByBoard[boardId] = replaceById(
        state.stagesByBoard[boardId],
        stage
      );
    })
    .addCase(deleteStage.fulfilled, (state, action) => {
      const { boardId, stageId } = action.payload;
      state.stagesByBoard[boardId] = (
        state.stagesByBoard[boardId] ?? []
      ).filter((stage) => stage.id !== stageId);
    })
    .addCase(reorderStages.fulfilled, (state, action) => {
      state.stagesByBoard[action.payload.boardId] = action.payload.stages;
    })

    // statuses
    .addCase(fetchStatuses.pending, (state) => {
      state.columnsLoading = true;
      state.error = null;
    })
    .addCase(fetchStatuses.fulfilled, (state, action) => {
      state.columnsLoading = false;
      state.statusesByBoard[action.payload.boardId] = action.payload.statuses;
    })
    .addCase(fetchStatuses.rejected, (state, action) => {
      state.columnsLoading = false;
      state.error = action.payload as string;
    })
    .addCase(createStatus.fulfilled, (state, action) => {
      const { boardId, status } = action.payload;
      const statuses = state.statusesByBoard[boardId] ?? [];
      statuses.push(status);
      state.statusesByBoard[boardId] = statuses;
    })
    .addCase(updateStatus.fulfilled, (state, action) => {
      const { boardId, status } = action.payload;
      state.statusesByBoard[boardId] = replaceById(
        state.statusesByBoard[boardId],
        status
      );
    })
    .addCase(deleteStatus.fulfilled, (state, action) => {
      const { boardId, statusId } = action.payload;
      state.statusesByBoard[boardId] = (
        state.statusesByBoard[boardId] ?? []
      ).filter((status) => status.id !== statusId);
    })
    .addCase(reorderStatuses.fulfilled, (state, action) => {
      state.statusesByBoard[action.payload.boardId] = action.payload.statuses;
    })

    // tasks
    .addCase(fetchBoardTasks.pending, (state) => {
      state.tasksLoading = true;
      state.error = null;
    })
    .addCase(fetchBoardTasks.fulfilled, (state, action) => {
      state.tasksLoading = false;
      state.tasksByBoard[action.payload.boardId] = action.payload.tasks;
    })
    .addCase(fetchBoardTasks.rejected, (state, action) => {
      state.tasksLoading = false;
      state.error = action.payload as string;
    })
    .addCase(createBoardTask.fulfilled, (state, action) => {
      const { boardId, task } = action.payload;
      // API lists tasks created_at desc, so newest goes first
      const tasks = state.tasksByBoard[boardId] ?? [];
      tasks.unshift(task);
      state.tasksByBoard[boardId] = tasks;
    })
    // Optimistic column move. A drag must land instantly — waiting for the
    // PATCH round-trip makes the card visibly snap back to its old column and
    // then jump forward. The pre-flight values are stashed on the task so
    // `.rejected` can restore them without a second lookup.
    .addCase(updateBoardTask.pending, (state, action) => {
      const { boardId, taskId, dto } = action.meta.arg;
      // Only the column move is applied optimistically. Other field edits go
      // through the normal fulfilled path — they have no drag attached, so the
      // round-trip is not perceptible.
      if (dto.stage_id === undefined && dto.status_id === undefined) return;

      const task = (state.tasksByBoard[boardId] ?? []).find(
        (entry) => entry.id === taskId
      );
      if (!task) return;

      task[OPTIMISTIC] = {
        stage_id: task.stage_id,
        stage: task.stage,
        status_id: task.status_id,
        status: task.status,
      };

      if (dto.stage_id !== undefined) {
        task.stage_id = dto.stage_id;
        // `stage` is denormalised onto the task and the card renders its label
        // from there — patching only the id leaves a stale pill on a card that
        // has visibly moved. Resolve the new stage from the board's own list.
        task.stage =
          (state.stagesByBoard[boardId] ?? []).find(
            (stage) => stage.id === dto.stage_id
          ) ?? task.stage;
      }

      if (dto.status_id !== undefined) {
        task.status_id = dto.status_id;
        task.status =
          (state.statusesByBoard[boardId] ?? []).find(
            (status) => status.id === dto.status_id
          ) ?? task.status;
      }
    })
    .addCase(updateBoardTask.fulfilled, (state, action) => {
      const { boardId, task } = action.payload;
      state.tasksByBoard[boardId] = replaceById(
        state.tasksByBoard[boardId],
        task
      );
    })
    .addCase(updateBoardTask.rejected, (state, action) => {
      const { boardId, taskId } = action.meta.arg;
      const task = (state.tasksByBoard[boardId] ?? []).find(
        (entry) => entry.id === taskId
      );
      const previous = task?.[OPTIMISTIC];
      if (!task || !previous) return;

      task.stage_id = previous.stage_id;
      task.stage = previous.stage;
      task.status_id = previous.status_id;
      task.status = previous.status;
      delete task[OPTIMISTIC];
      // Deliberately not setting `state.error`: that field drives the page-level
      // "couldn't load task boards" screen. A failed card move is transient and
      // already surfaced by its own toast — writing it here would leave a stale
      // error string behind that the next render of an empty board would show.
    })
    .addCase(deleteBoardTask.fulfilled, (state, action) => {
      const { boardId, taskId } = action.payload;
      state.tasksByBoard[boardId] = (state.tasksByBoard[boardId] ?? []).filter(
        (task) => task.id !== taskId
      );
    })
    // archive / restore
    .addCase(fetchArchivedTasks.pending, (state) => {
      state.archivedLoading = true;
      state.archivedError = null;
    })
    .addCase(fetchArchivedTasks.fulfilled, (state, action) => {
      const { boardId, tasks } = action.payload;
      state.archivedLoading = false;
      state.archivedByBoard[boardId] = tasks;
    })
    .addCase(fetchArchivedTasks.rejected, (state, action) => {
      state.archivedLoading = false;
      state.archivedError = action.payload as string;
    })
    // Archiving moves a task between the two buckets. The server echo is the
    // source of truth for the moved row — it carries the freshly stamped
    // `archived_at` the row renders.
    .addCase(archiveBoardTask.fulfilled, (state, action) => {
      const { boardId, task } = action.payload;
      state.tasksByBoard[boardId] = (state.tasksByBoard[boardId] ?? []).filter(
        (entry) => entry.id !== task.id
      );
      const archived = state.archivedByBoard[boardId];
      // Only seed the bucket when it has been fetched; leaving it undefined
      // keeps "never loaded" distinct from "loaded and empty", so the archive
      // page still fetches on first visit.
      if (archived) {
        state.archivedByBoard[boardId] = [
          task,
          ...archived.filter((entry) => entry.id !== task.id),
        ];
      }
    })
    .addCase(restoreBoardTask.fulfilled, (state, action) => {
      const { boardId, task } = action.payload;
      state.archivedByBoard[boardId] = (
        state.archivedByBoard[boardId] ?? []
      ).filter((entry) => entry.id !== task.id);
      const active = state.tasksByBoard[boardId];
      if (active) {
        state.tasksByBoard[boardId] = [
          task,
          ...active.filter((entry) => entry.id !== task.id),
        ];
      }
    })

    // ui
    .addCase(setActiveBoard, (state, action) => {
      state.activeBoardId = action.payload;
    })

    .addCase(logout, () => initialState);
});
